<template>
  <ion-page>
    <ion-content :class="'main-background'" v-show="!authenticated" :fullscreen="true" slot="fixed" :scroll-y="true">
      <div class="logo" v-show="showLogo">
        <img src="@/theme/liga-logo-white.png" alt="liga-logo" />
      </div>
      <div class="login-card-wrapper">
        <ion-card :class="['bg-transparent', showLogo ? '' : 'half-screen-top']">
          <ion-card-header class="ion-text-center white-bold">АВТОРИЗАЦИЯ</ion-card-header>
          <ion-card-content>
            <ion-item class="bg-transparent" lines="none">
              <ion-input label="Логин" label-placement="floating" fill="solid" type="text" v-model="loginData.username"
                :disabled="inlineLoading" @ionFocus="onInputFocus()" autocomplete="off" autocorrect="off" @keyup.enter="singIn"></ion-input>
            </ion-item>

            <ion-item class="bg-transparent" lines="none">
              <ion-input label="Пароль" label-placement="floating" fill="solid" type="password"
                v-model="loginData.password" :disabled="inlineLoading" @ionFocus="onInputFocus()" autocomplete="off"
                autocorrect="off" @keyup.enter="singIn"></ion-input>
            </ion-item>

            <div class="main-button-container">
              <ion-button @click="singIn()" :disabled="inlineLoading" type="submit">
                <p v-show="!inlineLoading">Вход</p>
                <ion-spinner name="bubbles" v-show="inlineLoading"></ion-spinner>
              </ion-button>
            </div>
            <div class="signup-container">
              <div @click="navigate('signup')">Регистрация</div>
              <div @click="navigate('reset-password')">Забыли пароль?</div>
            </div>
            <div class="not-authorized" @click="navigate('today')">Пропустить</div>
          </ion-card-content>
        </ion-card>
      </div>
      <div class="login-legal">
        <div class="login-legal-links">
          <a :href="LEGAL.offerUrl" target="_blank" rel="noopener noreferrer" class="login-legal-link">Договор оферты</a>
          <span @click="navigate('privacy-policy')" class="login-legal-link">Политика конфиденциальности</span>
          <span @click="navigate('offer-supplement')" class="login-legal-link">Условия возврата</span>
        </div>
        <div class="login-legal-payee">
          <p class="login-legal-payee-title">Платежи принимает:</p>
          <p class="login-legal-payee-name">{{ LEGAL.payee.fullName }}</p>
          <p>ИНН {{ LEGAL.payee.inn }} · ОГРНИП {{ LEGAL.payee.ogrnip }}</p>
          <p>{{ LEGAL.payee.address }}</p>
          <p>Тел.: {{ LEGAL.payee.phone }} · {{ LEGAL.payee.email }}</p>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import router from "@/router/index";
import AccountService from "@/services/account.service";
import store from "@/store/index";
import { Browser } from "@capacitor/browser";
import { Device } from "@capacitor/device";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonContent,
    IonInput,
    IonItem,
    IonPage,
    IonSpinner,
    alertController,
    toastController,
} from "@ionic/vue";
import { defineComponent, inject } from "vue";
import { LEGAL } from "@/config/legal";

export default defineComponent({
  props: {
    timeout: { type: Number, default: 1000 },
  },
  name: "Login",
  components: {
    IonContent,
    IonPage,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonInput,
    IonButton,
    IonSpinner,
    IonItem,
  },
  setup() {
    const accountService = inject("accountService") as AccountService;
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;

    return {
      accountService,
      isMobile,
      globalContext,
      LEGAL,
    };
  },
  data() {
    return {
      loginData: {
        username: "",
        password: "",
      },
      login: null,
      password: null,
      rememberMe: "",
      showLogo: true,
      inlineLoading: false,
    };
  },
  computed: {
    account() {
      return store.getters.account;
    },
    authenticated() {
      return store.getters.authenticated;
    },
  },
  created() {
    this.inlineLoading = true;
    this.accountService
      .hasAnyAuthorityAndCheckAuth(process.env.VUE_APP_INSTRUCTOR_ROLE)
      .then((response) => {
        this.inlineLoading = false;
        if (response) {
          this.successLogin();
        } else {
          Preferences.remove({ key: 'jwt' });
          store.commit('logout');
          return;
        }
      });
  },
  ionViewWillEnter() {
    this.showLogo = true;
    if (this.globalContext.offlineMode) {
      router.replace("today");
      return;
    }
    const sessionExpired = this.$route.query.sessionExpired === '1';
    if (sessionExpired) {
      router.replace({ path: '/login', query: {} });
      toastController.create({
        message: 'Сессия истекла. Войдите снова.',
        duration: 4000,
        color: 'warning',
      }).then(t => t.present());
    }
  },
  methods: {
    navigate(routeName: string): void {
      router.push(`/${routeName}`);
    },

    onInputFocus() {
      if (this.isMobile) this.showLogo = false;
    },

    onInputBlur() {
      if (this.isMobile) this.showLogo = true;
    },

    async singIn() {
      this.showLogo = true;
      this.inlineLoading = true;

      const deviceName = await this.getDeviceName();
      const data = {
        username: this.loginData.username,
        password: this.loginData.password,
        rememberMe: true,
        deviceName: deviceName || undefined,
      };

      const options = {
        url: `${process.env.VUE_APP_API_URL}api/authenticate`,
        data: data,
        headers: { "Content-Type": "application/json" },
      };

      await CapacitorHttp.post(options)
        .then((response) => {
          // console.log('response', response);
          if (response.status !== 200 && response.status !== 1001) {
            this.showAlert("Ошибка входа", "Проверьте логин и пароль.");
          }

          const bearerToken = response.headers.Authorization || response.headers.authorization || response.headers["Authorization"];
          if (bearerToken && bearerToken.slice(0, 7) === "Bearer ") {
            const jwt = bearerToken.slice(7, bearerToken.length);

            if (jwt && jwt.length > 0) {
              Preferences.set({
                key: "jwt",
                value: jwt,
              });

              if (!this.isMobile) {
                localStorage.setItem("jhi-authenticationToken", jwt);
                sessionStorage.removeItem("jhi-authenticationToken");
              }

              this.accountService.retrieveProfiles();
              this.accountService.retrieveAccount().then(() => {
                this.successLogin();
              });
            } else {
              throw new Error("Bad jwt");
            }
          } else {
            throw new Error("Bad jwt");
          }
        })
        .catch((err) => {
          if (err.message === "Network Error" || err.status === 408) {
            this.$router.replace("/badconnection");
          } else {
            this.showAlert("Ошибка входа", "Проверьте логин и пароль.");
          }
        })
        .finally(() => {
          this.inlineLoading = false;
        });
    },

    successLogin() {
      this.inlineLoading = false;

      this.$nextTick(function () {
        if (!this.globalContext.offlineMode) {
          this.accountService
            .hasAnyAuthorityAndCheckAuth("ROLE_MANAGER")
            .then((value) => {
              this.globalContext.isManager = value;
            });
        } else {
          this.globalContext.isManager = false;
        }

        router.replace("today");
      });
    },

    async register() {
      await Browser.open({ url: `${process.env.VUE_APP_REGISTER_URL}` }).catch(
        (err) => {
          console.log(err);
        }
      );
    },

    /** Использует Capacitor Device API для мобильных и веба (см. https://capacitorjs.com/docs/apis/device). */
    async getDeviceName(): Promise<string> {
      const maxLen = 255;
      try {
        if (Capacitor.isNativePlatform()) {
          const info = await Device.getInfo();
          const parts: string[] = [];
          if (info.name?.trim()) parts.push(info.name.trim());
          else if (info.model?.trim()) parts.push(info.model.trim());
          if (info.platform === "ios") {
            parts.push(info.osVersion ? `iOS ${info.osVersion}` : "iOS");
          } else if (info.platform === "android") {
            if (info.manufacturer?.trim() && info.model?.trim()) parts.push(`${info.manufacturer} ${info.model}`);
            else if (info.model?.trim()) parts.push(info.model);
            parts.push(info.osVersion ? `Android ${info.osVersion}` : "Android");
          }
          const str = parts.length ? parts.join(", ") : "Мобильное приложение";
          return str.length > maxLen ? str.substring(0, maxLen) : str;
        }
      } catch {
        // fallback
      }
      if (typeof navigator !== "undefined" && navigator.userAgent) {
        const ua = navigator.userAgent;
        const str = ua.length > maxLen ? ua.substring(0, maxLen) : ua;
        return str || "Веб-браузер";
      }
      return "Веб-браузер";
    },

    async showAlert(alertTitle: string, alertMessage: string) {
      const alert = await alertController.create({
        header: alertTitle,
        message: alertMessage,
        buttons: ["OK"],
      });

      await alert.present();
    },
  },
});
</script>

<style scoped>
.logo {
  margin: 10vh 0 -6vh 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

.logo img {
  width: 300px;
}

.half-screen-top {
  margin-top: 50%;
}

ion-card-header {
  letter-spacing: 0.08em;
  font-size: 2em;
}

ion-input {
  --background: var(--ion-color-gray) !important;
  --border-radius: 25px;
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  margin-bottom: 2em;
  --color: var(--ion-color-black);
  --highlight-color-focused: var(--ion-color-gray);
  --padding-start: 2em !important;
}

ion-button {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  width: 280px;
  height: 50px;
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-weight: bold;
  --border-radius: 14px;
}

.main-button-container {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}

.signup-container {
  margin-top: 2vh;
  display: flex;
  flex: row nowrap;
  justify-content: space-between;
  color: white;
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  cursor: pointer;
}

.not-authorized {
  color: rgba(255, 255, 255, .5);
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  cursor: pointer;  
}

.login-card-wrapper {
  z-index: 10;
}

.login-legal {
  padding: 2vh 16px 4vh;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-family: 'Montserrat', Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}
.login-legal-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5em 1em;
  margin-bottom: 1.5em;
}
.login-legal-link {
  color: var(--ion-color-orange);
  cursor: pointer;
  text-decoration: none;
}
.login-legal-link:hover {
  text-decoration: underline;
}
.login-legal-payee {
  text-align: center;
  font-size: 0.85em;
  line-height: 1.5;
  opacity: 0.95;
}
.login-legal-payee-title {
  font-weight: 600;
  margin-bottom: 0.25em;
}
.login-legal-payee-name {
  font-weight: 600;
  margin-bottom: 0.35em;
}
.login-legal-payee p {
  margin: 0.2em 0;
}

/* 
  ##Device = Desktops
  ##Screen = 1281px to higher resolution desktops
*/

@media (min-width: 1281px) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}

/* 
  ##Device = Laptops, Desktops
  ##Screen = B/w 1025px to 1280px
*/

@media (min-width: 1025px) and (max-width: 1280px) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}

/* 
  ##Device = Tablets, Ipads (portrait)
  ##Screen = B/w 768px to 1024px
*/

@media (min-width: 768px) and (max-width: 1024px) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}

/* 
  ##Device = Tablets, Ipads (landscape)
  ##Screen = B/w 768px to 1024px
*/
@media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  .login-card-wrapper {
    display: flex;
    justify-content: center;
  }

  ion-card {
    width: 50%;
  }
}
</style>